import React, { useEffect, useRef, useState } from "react";
import { useToast } from "../../common/toast/ToastContext";
import AdminPageLayout from "../components/AdminPageLayout";
import {
  fetchMediaImages,
  uploadMediaImage,
  replaceMediaImage,
  updateMediaImageMeta,
  restoreMediaImage,
  softDeleteMediaImage,
  hardDeleteMediaImage,
  fetchMediaImageUses,
} from "./adminMediaImageService";
import "./mediaImage.css";

const BASE_ROOT = process.env.REACT_APP_BASE_URL_ROOT || process.env.REACT_APP_BASE_URL?.replace(/\/api$/, "");
const VISIBILITIES = ["PUBLIC", "PRIVATE"];
const PAGE_SIZE = 20;

const emptyUpload = { name: "", visibility: "PUBLIC", imageUse: "", altText: "" };

const getUseColorClass = (use) => {
  const map = { VOCABULARY: "vocabulary", GRAMMAR: "grammar", PROFILE: "profile", GENERAL: "general", INTERNAL: "internal", DRAFT: "draft" };
  return `mi-badge mi-badge-${map[use] || "general"}`;
};

const formatSize = (bytes) => {
  if (!bytes) return "";
  return bytes >= 1048576 ? `${(bytes / 1048576).toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`;
};

const formatDate = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const AdminMediaImagePage = () => {
  const { showToast } = useToast();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [filters, setFilters] = useState({ visibility: "", imageUse: "" });
  const [showDeleted, setShowDeleted] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadForm, setUploadForm] = useState(emptyUpload);
  const [uploadFile, setUploadFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [imageUses, setImageUses] = useState([]);
  const [usesLoading, setUsesLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const replaceRefs = useRef({});

  const load = async (p = page, f = filters, deleted = showDeleted) => {
    setLoading(true);
    const params = { page: p, size: PAGE_SIZE, ...f, ...(deleted ? { showDeleted: true } : {}) };
    console.log("[MediaImages] load params:", params);
    const res = await fetchMediaImages(params);
    console.log("[MediaImages] response:", res.status, res.data);
    if (res.status === 200) {
      const list = Array.isArray(res.data) ? res.data : res.data.content ?? [];
      setImages(list);
      setHasMore(Array.isArray(res.data) ? list.length === PAGE_SIZE : !res.data.last);
    } else {
      showToast(res.message || "Failed to load images", "error");
    }
    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(0, filters, showDeleted); setPage(0); }, [filters, showDeleted]);

  useEffect(() => {
    const loadUses = async () => {
      setUsesLoading(true);
      const res = await fetchMediaImageUses();
      if (res.status === 200) setImageUses(res.data || []);
      else showToast("Failed to load image uses", "error");
      setUsesLoading(false);
    };
    loadUses();
  }, []);

  const handleFilterChange = (key, val) => setFilters((f) => ({ ...f, [key]: val }));

  const handleUpload = async () => {
    if (!uploadFile || !uploadForm.name.trim()) { showToast("Name and file are required", "error"); return; }
    if (uploadFile.size > 5 * 1024 * 1024) { showToast("File must be under 5 MB", "error"); return; }
    setSaving(true);
    const res = await uploadMediaImage({ ...uploadForm, file: uploadFile });
    if (res.status === 200 || res.status === 201) {
      showToast("Image uploaded successfully", "success");
      setShowUpload(false); setUploadForm(emptyUpload); setUploadFile(null);
      load(0, filters); setPage(0);
    } else {
      showToast(res.message || "Upload failed", "error");
    }
    setSaving(false);
  };

  const handleReplace = async (id, e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { showToast("File must be under 5 MB", "error"); e.target.value = ""; return; }
    const res = await replaceMediaImage(id, f);
    if (res.status === 200) { showToast("Image replaced", "success"); load(); }
    else showToast(res.message || "Replace failed", "error");
    e.target.value = "";
  };

  const handleSaveMeta = async (id) => {
    setSaving(true);
    const res = await updateMediaImageMeta(id, editForm);
    if (res.status === 200) { showToast("Updated successfully", "success"); setEditingId(null); load(); }
    else showToast(res.message || "Update failed", "error");
    setSaving(false);
  };

  const handleRestore = async (id) => {
    const res = await restoreMediaImage(id);
    if (res.status === 200) { showToast("Image restored", "success"); load(); }
    else showToast(res.message || "Restore failed", "error");
  };

  const handleSoftDelete = async (id) => {
    if (!window.confirm("Soft delete this image?")) return;
    const res = await softDeleteMediaImage(id);
    if (res.status === 200 || res.status === 204) { showToast("Image deleted", "success"); load(); }
    else showToast(res.message || "Delete failed", "error");
  };

  const handleHardDelete = async (id) => {
    if (!window.confirm("This permanently deletes the file from disk and cannot be undone. Continue?")) return;
    const res = await hardDeleteMediaImage(id);
    if (res.status === 200 || res.status === 204) { showToast("Image permanently deleted", "success"); load(); }
    else showToast(res.message || "Hard delete failed", "error");
  };

  const startEdit = (img) => {
    setEditingId(img.id);
    setEditForm({ name: img.name, imageUse: img.imageUse, altText: img.altText || "" });
  };

  const changePage = (delta) => {
    const next = page + delta;
    setPage(next);
    load(next, filters, showDeleted);
  };

  return (
    <AdminPageLayout
      title="Media Images"
      subtitle="Manage all media images used across the platform."
      instruction="Upload images for vocabulary, grammar, profiles, and general use. Soft delete hides an image; hard delete permanently removes it from disk."
      actions={
        <button className="adm-btn adm-btn-primary" onClick={() => { setShowUpload(true); setUploadForm({ ...emptyUpload, imageUse: imageUses[0] || "" }); setUploadFile(null); }}>
          + Upload Image
        </button>
      }
    >
      {/* Filters */}
      <div className="mi-filters">
        <select value={filters.visibility} onChange={(e) => handleFilterChange("visibility", e.target.value)}>
          <option value="">All Visibility</option>
          {VISIBILITIES.map((v) => <option key={v}>{v}</option>)}
        </select>
        <select value={filters.imageUse} onChange={(e) => handleFilterChange("imageUse", e.target.value)}>
          <option value="">All Image Use</option>
          {imageUses.map((u) => <option key={u}>{u}</option>)}
        </select>
        <label className="mi-toggle">
          <input type="checkbox" checked={showDeleted} onChange={(e) => setShowDeleted(e.target.checked)} />
          Show Deleted
        </label>
      </div>

      {/* Upload Form */}
      {showUpload && (
        <div className="mi-form-card">
          <h3>Upload New Image</h3>
          <div className="mi-form-grid">
            <div className="form-group">
              <label>Name *</label>
              <input value={uploadForm.name} onChange={(e) => setUploadForm({ ...uploadForm, name: e.target.value })} placeholder="Image name" />
            </div>
            <div className="form-group">
              <label>Visibility *</label>
              <select value={uploadForm.visibility} onChange={(e) => setUploadForm({ ...uploadForm, visibility: e.target.value })}>
                {VISIBILITIES.map((v) => <option key={v}>{v}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Image Use *</label>
              <select
                value={uploadForm.imageUse}
                onChange={(e) => setUploadForm({ ...uploadForm, imageUse: e.target.value })}
                disabled={usesLoading}
              >
                {usesLoading
                  ? <option>Loading…</option>
                  : imageUses.map((u) => <option key={u}>{u}</option>)
                }
              </select>
            </div>
            <div className="form-group">
              <label>Alt Text</label>
              <input value={uploadForm.altText} onChange={(e) => setUploadForm({ ...uploadForm, altText: e.target.value })} placeholder="Optional alt text" />
            </div>
            <div className="form-group">
              <label>File * (JPEG/PNG/WebP, max 5MB)</label>
              <input type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => setUploadFile(e.target.files[0])} />
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="adm-btn adm-btn-primary" onClick={handleUpload} disabled={saving}>
              {saving ? "Uploading…" : "Upload"}
            </button>
            <button className="adm-btn adm-btn-ghost" onClick={() => setShowUpload(false)}>Cancel</button>
          </div>
        </div>
      )}

      {/* Grid */}
      {loading ? (
        <p className="adm-loading">Loading images…</p>
      ) : images.length === 0 ? (
        <p className="adm-empty">No images found.</p>
      ) : (
        <div className="mi-grid">
          {images.map((img) => (
            <div key={img.id} className={`mi-card${img.deleted ? " mi-card-deleted" : ""}`}>
              <img
                src={`${BASE_ROOT}${img.url}`}
                alt={img.altText || img.name}
                className="mi-thumb"
              />
              <div className="mi-body">
                <span className="mi-name">{img.name}</span>
                <div className="mi-badges">
                  <span className={getUseColorClass(img.imageUse)}>{img.imageUse}</span>
                  <span className={`mi-badge ${img.visibility === "PUBLIC" ? "mi-badge-public" : "mi-badge-private"}`}>{img.visibility}</span>
                </div>
                {img.altText && <span className="mi-alt">{img.altText}</span>}
                <span className="mi-meta">
                  {formatSize(img.fileSizeBytes)}{img.fileSizeBytes && img.createdAt ? " · " : ""}{formatDate(img.createdAt)}
                </span>
              </div>

              {/* Inline Edit Form */}
              {editingId === img.id && (
                <div className="mi-inline-edit">
                  <div>
                    <label>Name</label>
                    <input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
                  </div>
                  <div>
                    <label>Image Use</label>
                    <select value={editForm.imageUse} onChange={(e) => setEditForm({ ...editForm, imageUse: e.target.value })}>
                      {imageUses.map((u) => <option key={u}>{u}</option>)}
                    </select>
                  </div>
                  <div>
                    <label>Alt Text</label>
                    <input value={editForm.altText} onChange={(e) => setEditForm({ ...editForm, altText: e.target.value })} />
                  </div>
                  <div className="mi-inline-edit-actions">
                    <button className="adm-btn adm-btn-primary adm-btn-sm" onClick={() => handleSaveMeta(img.id)} disabled={saving}>Save</button>
                    <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={() => setEditingId(null)}>Cancel</button>
                  </div>
                </div>
              )}

              <div className="mi-actions">
                {img.deleted ? (
                  <>
                    <button className="adm-btn adm-btn-primary adm-btn-sm" onClick={() => handleRestore(img.id)}>Restore</button>
                    <button className="adm-btn adm-btn-danger adm-btn-sm" onClick={() => handleHardDelete(img.id)}>Hard Del</button>
                  </>
                ) : (
                  <>
                    <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={() => editingId === img.id ? setEditingId(null) : startEdit(img)}>
                      {editingId === img.id ? "Close" : "Edit Meta"}
                    </button>
                    <input type="file" accept="image/jpeg,image/png,image/webp" style={{ display: "none" }}
                      ref={(el) => (replaceRefs.current[img.id] = el)}
                      onChange={(e) => handleReplace(img.id, e)}
                    />
                    <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={() => replaceRefs.current[img.id]?.click()}>Replace</button>
                    <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={() => { navigator.clipboard.writeText(`${BASE_ROOT}${img.url}`); showToast("URL copied", "success"); }}>Copy URL</button>
                    <button className="adm-btn adm-btn-warning adm-btn-sm" onClick={() => handleSoftDelete(img.id)}>Soft Del</button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {!loading && (images.length > 0 || page > 0) && (
        <div className="mi-pagination">
          <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={() => changePage(-1)} disabled={page === 0}>‹ Prev</button>
          <span>Page {page + 1}</span>
          <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={() => changePage(1)} disabled={!hasMore}>Next ›</button>
        </div>
      )}
    </AdminPageLayout>
  );
};

export default AdminMediaImagePage;
